"use client";

import { ChatMode } from "@/types/frontend-types";
import { LogLevel, type useSeqLog } from "@/hooks/telemetry";
import type { SessionDataContextValue } from "../data/context";
import type {
  CollabRef,
  CollabReferenceResolution,
  FileReferenceResolution,
  ProductReferenceResolution,
  VersionReferenceResolution,
} from "./reference-navigation-resolvers";
import {
  CollabReferenceResolutionKind,
  FileReferenceResolutionKind,
  isCollabAlreadyTargeted,
  ProductReferenceResolutionKind,
  VersionReferenceResolutionKind,
} from "./reference-navigation-resolvers";
import { NavigationOutcome } from "./reference-navigation-types";
import type {
  AfterCollabReferenceNavigateHandler,
  SessionUIContextValue,
} from "./session-ui-context";

const PRODUCT_FOCUS_FRAME_DELAY_MS = 16;
const CHAT_MODE_LABEL_MAIN = "Main";
const CHAT_MODE_LABEL_PRODUCT = "Product";

type ReferenceNavigationLog = ReturnType<typeof useSeqLog>;
type SessionActions = SessionDataContextValue["actions"];
type ReferenceNavigateCallback = () => void;

enum CollabNavigationBranch {
  Main = "main",
  ProductCached = "product-cached",
  ProductUncached = "product-uncached",
}

interface ProductReferenceTargetInput {
  actions: SessionActions;
  callBeforeNavigate: ReferenceNavigateCallback;
  focusedProductId: string | null;
  seqLog: ReferenceNavigationLog;
  shortId: string | null | undefined;
  target: ProductReferenceResolution;
}

interface VersionReferenceTargetInput {
  actions: SessionActions;
  callBeforeNavigate: ReferenceNavigateCallback;
  focusedProductId: string | null;
  seqLog: ReferenceNavigationLog;
  shortId: string | null | undefined;
  target: VersionReferenceResolution;
}

interface FileReferenceTargetInput {
  actions: SessionActions;
  callBeforeNavigate: ReferenceNavigateCallback;
  focusedProductId: string | null;
  openFilePreview: SessionUIContextValue["openFilePreview"];
  target: FileReferenceResolution;
}

interface CollabReferenceTargetInput {
  actions: SessionActions;
  callAfterCollabNavigate: AfterCollabReferenceNavigateHandler;
  callBeforeNavigate: ReferenceNavigateCallback;
  chat: SessionDataContextValue["chat"];
  products: SessionDataContextValue["products"];
  seqLog: ReferenceNavigationLog;
  shortId: string | null | undefined;
  target: CollabReferenceResolution;
}

interface ProductCollabNavigationInput {
  actions: SessionActions;
  callAfterCollabNavigate: AfterCollabReferenceNavigateHandler;
  collab: CollabRef;
  productIsCached: boolean;
  seqLog: ReferenceNavigationLog;
  targetProductId: string;
}

interface CompleteCollabNavigationInput {
  actions: SessionActions;
  branch: CollabNavigationBranch;
  callAfterCollabNavigate: AfterCollabReferenceNavigateHandler;
  chatMode: ChatMode;
  collab: CollabRef;
  seqLog: ReferenceNavigationLog;
  targetProductId: string | null;
}

export function navigateProductReferenceTarget({
  actions,
  callBeforeNavigate,
  focusedProductId,
  seqLog,
  shortId,
  target,
}: ProductReferenceTargetInput): NavigationOutcome {
  if (target.kind === ProductReferenceResolutionKind.ShortIdUnresolved) {
    seqLog(
      "P05 navigateToProductReference: unresolved short-id",
      {
        shortId,
      },
      LogLevel.Warning,
    );
    return NavigationOutcome.Unresolved;
  }

  if (target.kind === ProductReferenceResolutionKind.ProductUnresolved) {
    seqLog(
      "P05a navigateToProductReference: product not in cache",
      {
        shortId,
        productId: target.productId,
      },
      LogLevel.Warning,
    );
    return NavigationOutcome.Unresolved;
  }

  return navigateReadyProductReference({
    actions,
    callBeforeNavigate,
    focusedProductId,
    seqLog,
    shortId,
    target,
  });
}

export function navigateVersionReferenceTarget({
  actions,
  callBeforeNavigate,
  focusedProductId,
  seqLog,
  shortId,
  target,
}: VersionReferenceTargetInput): NavigationOutcome {
  if (target.kind === VersionReferenceResolutionKind.ShortIdUnresolved) {
    seqLog(
      "V05 navigateToVersionReference: unresolved short-id",
      {
        shortId,
      },
      LogLevel.Warning,
    );
    return NavigationOutcome.Unresolved;
  }

  if (target.kind === VersionReferenceResolutionKind.ProductUnresolved) {
    seqLog(
      "V05a navigateToVersionReference: product unresolved",
      {
        shortId,
        versionId: target.versionId,
        productId: target.productId ?? null,
      },
      LogLevel.Warning,
    );
    return NavigationOutcome.Unresolved;
  }

  return navigateReadyVersionReference({
    actions,
    callBeforeNavigate,
    focusedProductId,
    seqLog,
    shortId,
    target,
  });
}

export function navigateFileReferenceTarget({
  actions,
  callBeforeNavigate,
  focusedProductId,
  openFilePreview,
  target,
}: FileReferenceTargetInput): NavigationOutcome {
  if (target.kind === FileReferenceResolutionKind.ShortIdUnresolved) {
    return NavigationOutcome.Unresolved;
  }

  if (target.kind === FileReferenceResolutionKind.FileUnresolved) {
    return NavigationOutcome.Unresolved;
  }

  if (target.kind === FileReferenceResolutionKind.ProductUnresolved) {
    return NavigationOutcome.Unresolved;
  }

  return navigateReadyFileReference({
    actions,
    callBeforeNavigate,
    focusedProductId,
    openFilePreview,
    target,
  });
}

export function navigateCollabReferenceTarget({
  actions,
  callAfterCollabNavigate,
  callBeforeNavigate,
  chat,
  products,
  seqLog,
  shortId,
  target,
}: CollabReferenceTargetInput): NavigationOutcome {
  seqLog(
    "05 navigateToCollabReference entry",
    {
      shortId,
      chatMode: chat.mode,
      chatSelectedProductId: chat.selectedProductId,
      chatTargetMessageId: chat.targetMessageId,
    },
    LogLevel.Info,
  );

  if (target.kind === CollabReferenceResolutionKind.ShortIdUnresolved) {
    seqLog(
      "05a navigateToCollabReference: unresolved short-id",
      {
        shortId,
      },
      LogLevel.Warning,
    );
    return NavigationOutcome.Unresolved;
  }

  if (target.kind === CollabReferenceResolutionKind.CollabUnresolved) {
    seqLog(
      "05b navigateToCollabReference: collab not found",
      {
        collabId: target.collabId,
      },
      LogLevel.Warning,
    );
    return NavigationOutcome.Unresolved;
  }

  return navigateReadyCollabReference({
    actions,
    callAfterCollabNavigate,
    callBeforeNavigate,
    chat,
    collab: target.collab,
    products,
    seqLog,
  });
}

function navigateReadyProductReference(input: {
  actions: SessionActions;
  callBeforeNavigate: ReferenceNavigateCallback;
  focusedProductId: string | null;
  seqLog: ReferenceNavigationLog;
  shortId: string | null | undefined;
  target: Extract<
    ProductReferenceResolution,
    { kind: ProductReferenceResolutionKind.Ready }
  >;
}): NavigationOutcome {
  const target = input.target;
  input.seqLog(
    "P06 navigateToProductReference: resolved",
    {
      shortId: input.shortId,
      targetProductId: target.productId,
      targetIsRoot: target.targetParentProductId == null,
      targetParentProductId: target.targetParentProductId,
      currentFocusedProductId: input.focusedProductId,
      currentFocusedIsRoot: input.focusedProductId
        ? target.currentFocusedParentProductId == null
        : null,
    },
    LogLevel.Info,
  );

  if (input.focusedProductId === target.productId) {
    input.seqLog(
      "P06a navigateToProductReference: already focused (Noop)",
      {
        productId: target.productId,
      },
      LogLevel.Info,
    );
    return NavigationOutcome.Noop;
  }

  input.seqLog(
    "P07 navigateToProductReference: calling focusProduct (push-only; does NOT rebuild stack from hierarchy)",
    {
      targetProductId: target.productId,
      targetIsRoot: target.targetParentProductId == null,
      currentFocusedProductId: input.focusedProductId,
    },
    LogLevel.Warning,
  );
  input.callBeforeNavigate();
  input.actions.focusProduct(target.productId);
  input.actions.clearChatTargets();
  return NavigationOutcome.Navigated;
}

function navigateReadyVersionReference(input: {
  actions: SessionActions;
  callBeforeNavigate: ReferenceNavigateCallback;
  focusedProductId: string | null;
  seqLog: ReferenceNavigationLog;
  shortId: string | null | undefined;
  target: Extract<
    VersionReferenceResolution,
    { kind: VersionReferenceResolutionKind.Ready }
  >;
}): NavigationOutcome {
  const target = input.target;
  input.seqLog(
    "V06 navigateToVersionReference: resolved",
    {
      shortId: input.shortId,
      versionId: target.versionId,
      targetProductId: target.productId,
      targetIsRoot: target.targetParentProductId == null,
      targetParentProductId: target.targetParentProductId,
      currentFocusedProductId: input.focusedProductId,
      displayedVersionId: target.displayedVersionId,
    },
    LogLevel.Info,
  );

  if (target.displayedVersionId === target.versionId) {
    input.seqLog(
      "V06a navigateToVersionReference: already displayed (Noop)",
      {
        versionId: target.versionId,
      },
      LogLevel.Info,
    );
    return NavigationOutcome.Noop;
  }

  input.seqLog(
    "V07 navigateToVersionReference: calling focusProduct (push-only; does NOT rebuild stack) + selectVersion",
    {
      targetProductId: target.productId,
      targetIsRoot: target.targetParentProductId == null,
      currentFocusedProductId: input.focusedProductId,
      versionId: target.versionId,
    },
    LogLevel.Warning,
  );
  input.callBeforeNavigate();
  input.actions.focusProduct(target.productId);
  void input.actions.ensureProductVersions(target.productId).then(() => {
    runAfterFrame(() => {
      input.actions.selectVersion(target.versionId);
      input.actions.clearChatTargets();
    });
  });
  return NavigationOutcome.Navigated;
}

function navigateReadyFileReference(input: {
  actions: SessionActions;
  callBeforeNavigate: ReferenceNavigateCallback;
  focusedProductId: string | null;
  openFilePreview: SessionUIContextValue["openFilePreview"];
  target: Extract<
    FileReferenceResolution,
    { kind: FileReferenceResolutionKind.Ready }
  >;
}): NavigationOutcome {
  const target = input.target;
  input.callBeforeNavigate();

  if (input.focusedProductId !== target.productId) {
    input.actions.focusProduct(target.productId);
  }

  const openPreview = (): void => {
    if (target.versionId) {
      input.actions.selectVersion(target.versionId);
    }
    input.actions.clearChatTargets();
    input.openFilePreview({
      initialFileId: target.file.id,
      files: target.files,
    });
  };

  if (target.versionId) {
    void input.actions.ensureProductVersions(target.productId).then(() => {
      runAfterFrame(openPreview);
    });
    return NavigationOutcome.Navigated;
  }

  runAfterFrame(openPreview);
  return NavigationOutcome.Navigated;
}

function navigateReadyCollabReference(input: {
  actions: SessionActions;
  callAfterCollabNavigate: AfterCollabReferenceNavigateHandler;
  callBeforeNavigate: ReferenceNavigateCallback;
  chat: SessionDataContextValue["chat"];
  collab: CollabRef;
  products: SessionDataContextValue["products"];
  seqLog: ReferenceNavigationLog;
}): NavigationOutcome {
  input.seqLog(
    "06 navigateToCollabReference: collab resolved",
    {
      collabId: input.collab.id,
      onProduct: input.collab.onProduct,
    },
    LogLevel.Info,
  );

  if (
    isCollabAlreadyTargeted({
      chat: input.chat,
      collab: input.collab,
    })
  ) {
    input.actions.setChatTargetMessage(input.collab.id);
    input.seqLog(
      "06a navigateToCollabReference: already targeted (Noop; replayed target request)",
      {
        collabId: input.collab.id,
      },
      LogLevel.Info,
    );
    return NavigationOutcome.Noop;
  }

  input.seqLog(
    "07 navigateToCollabReference: calling callBeforeNavigate",
    {
      collabId: input.collab.id,
    },
    LogLevel.Info,
  );
  input.callBeforeNavigate();
  input.seqLog(
    "08 navigateToCollabReference: callBeforeNavigate returned",
    {
      collabId: input.collab.id,
    },
    LogLevel.Info,
  );

  if (input.collab.onProduct == null) {
    navigateMainCollabReference(input);
    return NavigationOutcome.Navigated;
  }

  navigateProductCollabReference({
    actions: input.actions,
    callAfterCollabNavigate: input.callAfterCollabNavigate,
    collab: input.collab,
    productIsCached: Boolean(input.products[input.collab.onProduct]),
    seqLog: input.seqLog,
    targetProductId: input.collab.onProduct,
  });
  return NavigationOutcome.Navigated;
}

function navigateMainCollabReference(input: {
  actions: SessionActions;
  callAfterCollabNavigate: AfterCollabReferenceNavigateHandler;
  collab: CollabRef;
  seqLog: ReferenceNavigationLog;
}): void {
  input.seqLog(
    "09 navigateToCollabReference: branch=main (no product)",
    {
      collabId: input.collab.id,
    },
    LogLevel.Info,
  );
  void completeCollabNavigation({
    ...input,
    branch: CollabNavigationBranch.Main,
    chatMode: ChatMode.Main,
    targetProductId: null,
  });
}

function navigateProductCollabReference(
  input: ProductCollabNavigationInput,
): void {
  if (input.productIsCached) {
    navigateCachedProductCollabReference(input);
    return;
  }
  navigateUncachedProductCollabReference(input);
}

function navigateCachedProductCollabReference(
  input: ProductCollabNavigationInput,
): void {
  input.seqLog(
    "09 navigateToCollabReference: branch=product-cached",
    {
      collabId: input.collab.id,
      targetProductId: input.targetProductId,
    },
    LogLevel.Info,
  );
  input.actions.focusProduct(input.targetProductId);
  input.seqLog(
    "11 navigateToCollabReference[product-cached]: focusProduct returned (sync)",
    {
      targetProductId: input.targetProductId,
    },
    LogLevel.Info,
  );

  void input.actions.ensureProductCollabs(input.targetProductId).then(() => {
    input.seqLog(
      "13 navigateToCollabReference[product-cached]: ensureProductCollabs resolved",
      {
        collabId: input.collab.id,
        targetProductId: input.targetProductId,
      },
      LogLevel.Info,
    );
    void completeCollabNavigation({
      ...input,
      branch: CollabNavigationBranch.ProductCached,
      chatMode: ChatMode.Product,
    });
  });
}

function navigateUncachedProductCollabReference(
  input: ProductCollabNavigationInput,
): void {
  input.seqLog(
    "09 navigateToCollabReference: branch=product-uncached",
    {
      collabId: input.collab.id,
      targetProductId: input.targetProductId,
    },
    LogLevel.Info,
  );
  void completeCollabNavigation({
    ...input,
    branch: CollabNavigationBranch.ProductUncached,
    chatMode: ChatMode.Product,
  });
}

async function completeCollabNavigation({
  actions,
  branch,
  callAfterCollabNavigate,
  chatMode,
  collab,
  seqLog,
  targetProductId,
}: CompleteCollabNavigationInput): Promise<void> {
  seqLog(
    `20 navigateToCollabReference[${branch}]: setting ${chatModeLabel(chatMode)} chat mode before target`,
    {
      collabId: collab.id,
      ...(targetProductId ? { targetProductId } : {}),
    },
    LogLevel.Info,
  );
  await actions.setChatMode(chatMode, targetProductId ?? undefined);
  actions.setChatTargetMessage(collab.id);
  seqLog(
    `21 navigateToCollabReference[${branch}]: mode and target set; requesting chat surface`,
    {
      collabId: collab.id,
      ...(targetProductId ? { targetProductId } : {}),
    },
    LogLevel.Info,
  );
  callAfterCollabNavigate({
    chatMode,
    messageId: collab.id,
    productId: targetProductId,
  });
  seqLog(
    `22 navigateToCollabReference[${branch}]: callAfterCollabNavigate returned`,
    {
      collabId: collab.id,
    },
    LogLevel.Info,
  );
}

function chatModeLabel(chatMode: ChatMode): string {
  if (chatMode === ChatMode.Main) {
    return CHAT_MODE_LABEL_MAIN;
  }
  return CHAT_MODE_LABEL_PRODUCT;
}

function runAfterFrame(callback: () => void): void {
  if (
    typeof window === "undefined" ||
    typeof window.requestAnimationFrame !== "function"
  ) {
    setTimeout(callback, PRODUCT_FOCUS_FRAME_DELAY_MS);
    return;
  }

  window.requestAnimationFrame(() => {
    callback();
  });
}
